import { useEffect, useState } from "react";
import { GetCustomerPersonalInfo } from "../../../../common/network/Api";
import { registerSessionTeardown } from "../../../../common/utils/sessionTeardown";

/**
 * The client's display name for a History detail page's `ClientBadge`.
 *
 * The history detail payloads (`/history/prescription/{id}`,
 * `/history/inbody/{id}`) carry the client's id but not their name, and the
 * cross-client page has no selected client to read it from. So the name comes
 * from the customer profile, one small request per client, cached for the
 * session so moving between a client's records doesn't refetch it.
 *
 * `resolving` is derived, not set by the effect, so it is already true in the
 * very render the client id arrives in: a page that holds its loader on it
 * never shows a frame of header without the badge. Failures are swallowed —
 * a missing badge is not worth an error state.
 */

export interface HistoryClientName {
  name: string | null;
  /** A request for this client's name is out; hold the page's loader. */
  resolving: boolean;
}

const cache = new Map<string, string>();
// Names are one user's view of their clients: gone with the session.
registerSessionTeardown(() => cache.clear());

const nameOf = (profile: any): string =>
  [profile?.firstName, profile?.lastName]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ");

const useHistoryClientName = (
  customerID: string | null | undefined,
  enabled = true
): HistoryClientName => {
  // The client whose request has come back (with or without a name); the
  // cache holds the answer itself.
  const [settledFor, setSettledFor] = useState<string | null>(null);

  const wanted = enabled && Boolean(customerID);
  const cached = customerID ? cache.get(customerID) ?? null : null;
  const resolving =
    wanted && cached === null && settledFor !== (customerID ?? null);

  useEffect(() => {
    if (!wanted || !customerID || cache.has(customerID)) return;
    let cancelled = false;
    GetCustomerPersonalInfo(customerID)
      .then((profile) => {
        const resolved = nameOf(profile);
        if (resolved) cache.set(customerID, resolved);
      })
      .catch((error) => {
        console.warn("Couldn't load the client's name for the badge:", error);
      })
      .finally(() => {
        if (!cancelled) setSettledFor(customerID);
      });
    return () => {
      cancelled = true;
    };
  }, [customerID, wanted]);

  return { name: wanted ? cached : null, resolving };
};

export default useHistoryClientName;
